/*    
<summary>
  This functional component "AddUserForm" provides form to add new user or update existing user.
  Developer:Aashish Singh, Created Date:04-March-2024
</summary>
<returns>Returns JSX</returns>
*/
import React, { useEffect } from "react";
import { Modal, Button, Row, Col } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { Form, Formik, Field, useFormikContext } from "formik";
import { IUser } from "../../../models/IUser";
import SpinnerLoader from "../../../shared-components/Loader/SpinnerLoader";
import FormikFormInput from "../../../shared-components/FormikFormHandlers/FormikFormInput";
import LoaderButton from "../../../shared-components/Button/LoaderButton";
import { IAddEditDriver } from "../../../models/response/IDriverResponse";
import Label from "../../../shared-components/label/label";
import FormikFormSelectField from "../../../shared-components/FormikFormHandlers/FormikFormSelectField";
import { IOption, IOptionWithLimit } from "../../../models/ICommon";
import { getDriverValidateSchema } from "../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import FormikFormDatePicker from "../../../shared-components/FormikFormHandlers/FormikFormDatePicker";
import FormikFormInputForRegexCheck from "../../../shared-components/FormikFormHandlers/formik-form-input-for-regex-check";
import { formatMessage } from "../../../translations/formatMessage";
import { Tooltip } from "react-tooltip";
import {
  DATE_FORMAT_BACKEND,
  integerRegex,
} from "../../../constants/common-constant";
import moment from "moment";
import FormikDateFieldMUI from "../../../shared-components/FormikFormHandlers/FormikFormDatePickerMUI";
import DraggableModal from "../../../shared-components/Draggable/DraggableModal";

interface IProps {
  id: number;
  submitHandler: (data: IAddEditDriver) => void;
  onModalClosed: () => void;
  getDataInProgress: boolean;
  addUpdateInProgress: boolean;
  initialValues: IAddEditDriver;
  allocationPeriodTypes: IOption[];
  customerIdList: IOptionWithLimit[];
  isLimitSettingEnabled: boolean;
}

const AddUpdateDriverForm = (props: IProps) => {
  const {
    id,
    submitHandler,
    onModalClosed,
    getDataInProgress,
    addUpdateInProgress,
    initialValues,
    allocationPeriodTypes,
    customerIdList,
    isLimitSettingEnabled,
  } = props;

  const FormObserver: React.FC = () => {
    const { values, setFieldValue } = useFormikContext<IAddEditDriver>();

    useEffect(() => {
      if (!values.BelongToCustomer) {
        setFieldValue("CustomerId", "-1");
      }
    }, [values.BelongToCustomer]);
    return null;
  };

  return (
    <>
      <Modal
        className="add-user-modal"
        show={true}
        centered
        size="lg"
        dialogAs={DraggableModal}
      >
        <Modal.Header>
          <Modal.Title className="fs-5 ps-3">
            {id === -1 ? (
              <FormattedMessage id="add_driver" />
            ) : (
              <FormattedMessage id="update_driver" />
              // <>
              //   <FormattedMessage id="update_driver" />&nbsp;
              //   {
              //     initialValues.UpdatedAt &&
              //     <span className="ml-2">(<FormattedMessage id="last_updated" />: {initialValues.UpdatedAt})</span>
              //   }
              // </>
            )}
          </Modal.Title>
          <div onClick={onModalClosed} className="icons8-close">
            <span className="material-symbols-outlined fs-5 text-dark">
              close
            </span>
          </div>
          {getDataInProgress ? <SpinnerLoader /> : null}
        </Modal.Header>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={getDriverValidateSchema(
            new Date(),
            isLimitSettingEnabled,
            customerIdList
          )}
          onSubmit={async (values: any) => {
            let tempValues = {
              RFID: values.RFID ? values.RFID.trim() : values.RFID,
              DriverName: values.DriverName
                ? values.DriverName.trim()
                : values.DriverName,
              DLNo: values.DLNo ? values.DLNo.trim() : values.DLNo,
              DLExpiryDate: values.DLExpiryDate,
              TruckAllocation1: values.TruckAllocation1
                ? values.TruckAllocation1.trim()
                : values.TruckAllocation1,
              TruckAllocation: values.TruckAllocation,
              FuelDeliveryEnabled: values.FuelDeliveryEnabled,
              AllocationPerFill: values.AllocationPerFill,
              AllocationPeriod:
                values.AllocationPeriod === -1 ? 0 : values.AllocationPeriod,
              AllocationVolume: values.AllocationVolume,
              RemainingVolume: values.RemainingVolume,
              BelongToCustomer: values.BelongToCustomer,
              CustomerId: values.CustomerId,
              EmployeeId: values.EmployeeId
                ? values.EmployeeId.trim()
                : values.EmployeeId,
              CustomerName: values.CustomerName
                ? values.CustomerName.trim()
                : values.CustomerName,
              IsLimitEnabled:
                isLimitSettingEnabled &&
                (customerIdList.filter(
                  (item) => Number(item.Id) === Number(values.CustomerId)
                )[0]
                  ? customerIdList.filter(
                      (item) => Number(item.Id) === Number(values.CustomerId)
                    )[0].EnableLimitSettings
                  : false),
            };
            let DLExpiryDate =
              new Date(values.DLExpiryDate?.$d).toString() !== "Invalid Date"
                ? moment(values.DLExpiryDate.$d).format(DATE_FORMAT_BACKEND)
                : values.DLExpiryDate;
            submitHandler({ ...tempValues, DLExpiryDate: DLExpiryDate });
          }}
        >
          {({ values }) => (
            <Form className="user-form p-3" noValidate>
              <FormObserver />
              <Modal.Body>
                <Row>
                  <Col lg={4} md={6} xs={6}>
                    <div className="form-group">
                      <Label
                        className="form-label"
                        // required={true}
                        label="rfid"
                      />
                      <Field
                        data-testid="testRFID"
                        className="form-control"
                        name="RFID"
                        type="text"
                        validcharacter={["@", "#", "$", "-", "_", "."]}
                        invalidcharacter={[" "]}
                        placeholder="rfid"
                        as={FormikFormInput}
                      />
                    </div>
                  </Col>
                  <Col lg={4} md={6} xs={6}>
                    <div className="form-group">
                      <Label
                        className="form-label"
                        required={true}
                        label="driver_name"
                      />
                      <Field
                        data-testid="testDriverName"
                        className="form-control"
                        name="DriverName"
                        validcharacter={["@", "#", "$", "-", "_", "."]}
                        type="text"
                        placeholder="driver_name"
                        as={FormikFormInput}
                      />
                    </div>
                  </Col>
                  <Col lg={4} md={6} xs={6}>
                    <div className="form-group">
                      <Label
                        className="form-label"
                        required={true}
                        label="dl_no"
                      />
                      <Field
                        data-testid="testDLNo"
                        className="form-control"
                        name="DLNo"
                        validcharacter={["@", "#", "$", "-", "_", "."]}
                        type="text"
                        invalidcharacter={[" "]}
                        placeholder="dl_no"
                        as={FormikFormInput}
                      />
                    </div>
                  </Col>
                  <Col lg={4} md={6} xs={6}>
                    <div className="form-group date-picker-error">
                      <Label
                        className="form-label"
                        required={false}
                        label="dl_expiry_date"
                      />
                      <Field
                        name="DLExpiryDate"
                        as={FormikDateFieldMUI}
                        min={moment(new Date()).format(DATE_FORMAT_BACKEND)}
                      />
                      {/* <Field
                        data-testid="testDLExpiryDate"
                        className="form-control"
                        name="DLExpiryDate"
                        type="text"
                        mindate={new Date()}
                        placeholder="dl_expiry_date"
                        as={FormikFormDatePicker}
                      /> */}
                    </div>
                  </Col>
                  <Col lg={4} md={6} xs={6}>
                    <div className="form-group">
                      <Label className="form-label" label="truck_allocation" />
                      <Field
                        data-testid="testTruckAllocation"
                        className="form-control"
                        name="TruckAllocation1"
                        placeholder="truck_allocation"
                        as={FormikFormInput}
                        invalidcharacter={[" "]}
                      />
                    </div>
                  </Col>
                  {isLimitSettingEnabled &&
                    (customerIdList.filter(
                      (item) => Number(item.Id) === Number(values.CustomerId)
                    )[0]
                      ? customerIdList.filter(
                          (item) =>
                            Number(item.Id) === Number(values.CustomerId)
                        )[0].EnableLimitSettings
                      : false) && (
                      <>
                        <Col lg={4} md={6} xs={6}>
                          <div className="form-group">
                            <Label
                              className="form-label"
                              required={true}
                              label="allocation_per_fill"
                            />
                            <Field
                              data-testid="testAllocationPerFill"
                              className="form-control"
                              name="AllocationPerFill"
                              placeholder="allocation_per_fill"
                              regex={integerRegex}
                              as={FormikFormInputForRegexCheck}
                            />
                          </div>
                        </Col>
                        <Col lg={4} md={6} xs={6}>
                          <div className="form-group">
                            <Label
                              className="form-label"
                              required={true}
                              label="allocation_period"
                            />
                            <Field
                              className="form-control form-select"
                              aria-label="Default select example"
                              name="AllocationPeriod"
                              type="number"
                              as={FormikFormSelectField}
                              typeofoptionid="number"
                              options={allocationPeriodTypes}
                            />
                          </div>
                        </Col>
                        <Col lg={4} md={6} xs={6}>
                          <div className="form-group">
                            <Label
                              className="form-label"
                              required={true}
                              label="allocation_volume"
                            />
                            <Field
                              data-testid="testAllocationVolume"
                              className="form-control"
                              name="AllocationVolume"
                              placeholder="allocation_volume"
                              regex={integerRegex}
                              as={FormikFormInputForRegexCheck}
                            />
                          </div>
                        </Col>
                        <Col lg={4} md={6} xs={6}>
                          <div className="form-group">
                            <Label
                              className="form-label"
                              label="remaining_volume"
                            />
                            <Field
                              data-testid="testRemainingVolume"
                              className="form-control"
                              name="RemainingVolume"
                              value={
                                props.id === -1
                                  ? values.AllocationVolume
                                  : values.RemainingVolume
                              }
                              placeholder="remaining_volume"
                              regex={integerRegex}
                              disabled={true}
                              as={FormikFormInputForRegexCheck}
                            />
                          </div>
                        </Col>
                      </>
                    )}
                  <Col lg={4} md={6} xs={6}>
                    <div className="form-group">
                      <Label className="form-label" label="employee_id" />
                      <Field
                        data-testid="testEmployeeId"
                        className="form-control"
                        name="EmployeeId"
                        placeholder="employee_id"
                        validcharacter={["@", "#", "$", "-", "_", ".", " "]}
                        as={FormikFormInput}
                      />
                    </div>
                  </Col>
                  {values.BelongToCustomer && (
                    <Col lg={4} md={6} xs={6}>
                      <div className="form-group">
                        <Label
                          className="form-label"
                          required={true}
                          label="customer_id"
                        />
                        <Field
                          className="form-control form-select"
                          aria-label="Default select example"
                          name="CustomerId"
                          type="number"
                          as={FormikFormSelectField}
                          typeofoptionid="string"
                          options={customerIdList}
                        />
                      </div>
                    </Col>
                  )}
                  <Col lg={8} md={6} xs={6}></Col>
                </Row>
                <Row>
                  <Col lg={4} md={6} xs={12}>
                    <div className="form-group witchBox">
                      <div className="form-check form-switch">
                        <Field
                          data-testid="testRememberMe"
                          className="form-check-input"
                          name="FuelDeliveryEnabled"
                          type="checkbox"
                          id="FuelDeliveryEnabled"
                        />
                        <label
                          htmlFor="FuelDeliveryEnabled"
                          className="form-check-label mt-1"
                        >
                          <FormattedMessage id="fuel_delivery_enabled" />
                        </label>
                      </div>
                    </div>
                  </Col>
                  <Col lg={4} md={6} xs={12}>
                    <div className="form-group witchBox">
                      <div className="form-check form-switch">
                        <Field
                          data-testid="testRememberMe"
                          className="form-check-input"
                          name="BelongToCustomer"
                          type="checkbox"
                          id="Self"
                          disabled={customerIdList.length <= 1 ? true : false}
                        />
                        <label htmlFor="Self" className="form-check-label mt-1">
                          <FormattedMessage id="belongs_to_customer" />
                        </label>
                        {customerIdList.length <= 1 && (
                          <div className="d-inline p-2">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="18"
                              fill="currentColor"
                              className="bi bi-info-circle ml-2"
                              viewBox="0 0 16 16"
                              data-tooltip-id="my-tooltip"
                              data-tooltip-html={formatMessage(
                                "no_customer_available"
                              )}
                              data-tooltip-variant="dark"
                            >
                              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                              <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                            </svg>
                            <Tooltip id="my-tooltip" place="right" />
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Modal.Body>
              <div className="modal-footer d-flex justify-content-center border-0">
                <LoaderButton
                  type="submit"
                  isLoading={addUpdateInProgress}
                  disabled={addUpdateInProgress}
                  text={id === -1 ? "button_save" : "button_edit"}
                />
                <Button
                  variant="btn-secondary bg-light btn-light ms-2"
                  onClick={onModalClosed}
                >
                  <FormattedMessage id="button_cancel" />
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
};

export default AddUpdateDriverForm;
